import { useMemo, useState } from 'react';
import {
  IconAlarm, IconCamera, IconChevronDown, IconChevronLeft, IconClipboardCheck, IconDots, IconEye, IconFileText, IconInfoCircle, IconListDetails, IconMessageCircle, IconPackage, IconSearch, IconUpload,
} from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { Page, PageHeader } from '@hana/shell';
import './svccompact.i18n';
import { Button, Drawer, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { defOf, kpiOf, leftLabel, leftShort, openCount, selectTasks, stepOf } from './logic';
import { PAD_IDS, PAD_TABS, PHONE_ME, PHONE_PATCH, docRows, gateLine, phoneGate, photoRows, shipRows, stepRows, type PhoneGate } from './compact';
import type { ServiceTask, SvcCode, SvcStatus } from './types';
import b from './services.module.css';
import s from './serviceCompact.module.css';

// Mint-Service-iPad (danh sách + trang chi tiết 2:1 + Kanban) · Mint-Service-Phone (Việc dịch vụ + ảnh hiện trường). Chỉ đọc; nút ghi báo toast.
// Không có tiền ở màn này: Chi phí & doanh thu / chi phí nội bộ nằm sau quyền `money` (chưa mở).

const PAD_ME = 'HOATTN';
const ACT: Record<string, string> = { Xem: 'svc.act.view', 'Chụp': 'svc.act.shoot', 'Chuyển bước': 'svc.act.move' };
type Tab = 'all' | SvcCode;
type Scope = 'mine' | 'team';
const ST_CLS: Record<SvcStatus, string> = { New: 'stNew', Processing: 'stPro', 'Chờ bên ngoài': 'stWait', Finish: 'stFin' };
const ST_DOT: Record<SvcStatus, string> = { New: 'var(--d-amber)', Processing: 'var(--d-blue)', 'Chờ bên ngoài': 'var(--d-violet)', Finish: 'var(--d-green)' };
const KANBAN: readonly SvcStatus[] = ['New', 'Processing', 'Chờ bên ngoài', 'Finish'];
const PHONE_SEG = [{ key: 'open', label: 'svc.seg.open' }, { key: 'new', label: 'svc.seg.new' }, { key: 'late', label: 'svc.seg.late' }, { key: 'done', label: 'svc.seg.done' }] as const;
type PhoneSeg = (typeof PHONE_SEG)[number]['key'];

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const isLate = (t: ServiceTask) => t.left < 0 && t.status !== 'Finish';
const leftTone = (t: ServiceTask) => {
  if (t.status === 'Finish' || t.status === 'Chờ bên ngoài') return '';
  if (t.left < 0) return s.err;
  return t.left <= 4 ? s.warn : '';
};

function StatusChip({ st }: { st: SvcStatus }) {
  return (
    <span className={`${b.st} ${b[ST_CLS[st]]}`}>
      <span className={b.stDot} style={{ background: ST_DOT[st] }} aria-hidden="true" />
      {st}
    </span>
  );
}

/** Thanh bước: xanh = đã qua, xanh dương = bước hiện tại (đỏ nếu trễ), xám = chưa tới. */
function Bar({ t }: { t: ServiceTask }) {
  const n = defOf(t.code).steps.length;
  return (
    <span className={s.bar} aria-hidden="true">
      {Array.from({ length: n }, (_, k) => (
        <i key={k} className={k < t.step || t.status === 'Finish' ? s.bOk : k === t.step ? (t.left < 0 ? s.bErr : s.bCur) : ''} />
      ))}
    </span>
  );
}

const Clock = ({ t }: { t: ServiceTask }) => (
  <span className={`${s.left} ${leftTone(t)}`} title={leftLabel(t)}>
    <IconAlarm size={12} aria-hidden="true" />
    {leftShort(t)}
  </span>
);

export function ServiceCompact({ phone }: { phone: boolean }) {
  const tr = useT();
  const toast = useToast();
  const { data, error, isLoading, isError, refetch } = useList('svcTask', { limit: 5000 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('all');
  const [scope, setScope] = useState<Scope>(phone ? 'mine' : 'team');
  const [q, setQ] = useState('');
  const [lateOnly, setLateOnly] = useState(false);
  const [urgOnly, setUrgOnly] = useState(false);
  const [seg, setSeg] = useState<PhoneSeg>('open');
  const [view, setView] = useState<'list' | 'kanban'>('list');
  const [open, setOpen] = useState<string | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  const [photo, setPhoto] = useState<{ id: string; slot: string } | null>(null);

  const me = phone ? PHONE_ME : PAD_ME;
  const all = useMemo(() => {
    const items = data?.items ?? [];
    return phone ? items.map((r) => ({ ...r, ...PHONE_PATCH[r.id] })) : PAD_IDS.flatMap((id) => items.filter((r) => r.id === id));
  }, [data, phone]);
  const noWrite = () => toast({ msg: tr('svc.noWrite') });

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const ready = state === 'ready';

  const stateProps = {
    state,
    title: state === 'empty' ? (phone ? tr('svc.emptyMine') : tr('svc.emptyOpen')) : undefined,
    desc: state === 'empty' ? (phone ? tr('svc.emptyMineDesc') : tr('svc.emptyOpenDesc')) : undefined,
    icon: state === 'empty' ? <IconClipboardCheck size={26} stroke={1.8} /> : undefined,
    action: state === 'error' ? <Button onClick={() => void refetch()}>{tr('svc.retry')}</Button> : undefined,
    skeleton: <Skeleton rows={5} />,
  };

  // ---------- Điện thoại ----------
  if (phone) {
    const mine = all.filter((r) => scope === 'team' || r.who === me);
    const bySeg: Record<PhoneSeg, ServiceTask[]> = {
      open: mine.filter((r) => r.status !== 'Finish'), new: mine.filter((r) => r.status === 'New'), late: mine.filter(isLate), done: mine.filter((r) => r.status === 'Finish'),
    };
    const rows = bySeg[seg];
    const cur = all.find((r) => r.id === photo?.id);
    if (photo && cur) return <PhotoScreen t={cur} slot={photo.slot} onBack={() => setPhoto(null)} />;
    const sh = all.find((r) => r.id === sheet);
    return (
      <Page kind="table">
        <PageHeader
          title={tr('svc.tasks')}
          actions={
            <Button onClick={() => setScope((v) => (v === 'mine' ? 'team' : 'mine'))} aria-pressed={scope === 'team'}>
              {scope === 'mine' ? tr('svc.mine') : tr('svc.team')}
            </Button>
          }
        />
        <div className={s.page}>
          <Segmented label={tr('svc.filter')} value={seg} onChange={(k) => setSeg(k as PhoneSeg)} items={PHONE_SEG.map((x) => ({ key: x.key, label: tr(x.label), n: ready ? bySeg[x.key].length : undefined }))} />
          <StateView {...stateProps} state={ready && rows.length === 0 ? 'empty' : state} title={ready ? tr('svc.noInSeg') : stateProps.title} icon={ready ? <IconClipboardCheck size={26} stroke={1.8} /> : stateProps.icon} desc={ready ? undefined : stateProps.desc}>
            <ul className={s.list}>
              {rows.map((r) => {
                const g = phoneGate(r);
                const st = stepOf(r);
                return (
                  <li key={r.id}>
                    <button type="button" className={s.pcard} onClick={() => setSheet(r.id)}>
                      <span className={s.top}>
                        <span className={s.l}><span className={s.code}>{r.code}</span><b className={s.ell}>{`${defOf(r.code).steps[Math.min(r.step, defOf(r.code).steps.length - 1)]} · ${st.no}`}</b></span>
                        <Clock t={r} />
                      </span>
                      <Bar t={r} />
                      <span className={s.sub}><span className={s.ell}>{r.job} · {r.customer}</span><span>{r.who}</span></span>
                      {g.length > 0 && <span className={s.gate}>{gateLine(g)}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </StateView>
        </div>
        <Drawer open={!!sh} onOpenChange={(o) => !o && setSheet(null)} title={tr('svc.sheetTitle')} description={sh ? `${sh.id} · ${sh.customer}` : undefined}>
          {sh && <PhoneSheet t={sh} onPhoto={(slot) => { setSheet(null); setPhoto({ id: sh.id, slot }); }} onNoWrite={noWrite} />}
        </Drawer>
      </Page>
    );
  }

  // ---------- iPad ----------
  const current = open ? all.find((r) => r.id === open) : undefined;
  if (current && ready) return <PadDetail t={current} onBack={() => setOpen(null)} />;

  const base = selectTasks(all, { me, scope, tab: 'all', text: q });
  const lateN = base.filter(isLate).length;
  const urgN = base.filter((r) => r.urgent && r.status !== 'Finish').length;
  const rows = base.filter((r) => (tab === 'all' || r.code === tab) && (!lateOnly || isLate(r)) && (!urgOnly || r.urgent));
  const tabItems = [{ key: 'all', label: tr('svc.all'), n: ready ? openCount(all, 'all') : undefined }, ...PAD_TABS.map((c) => ({ key: c as string, label: c, n: ready ? openCount(all, c) : undefined }))];

  return (
    <Page kind="table">
      <PageHeader
        title={tr('svc.title')}
        actions={
          <>
            <Segmented label={tr('svc.viewKind')} value={view} onChange={(k) => setView(k as 'list' | 'kanban')} items={[{ key: 'list', label: tr('svc.list') }, { key: 'kanban', label: 'Kanban' }]} />
            <Button icon={<IconMessageCircle size={16} aria-hidden="true" />} onClick={() => toast({ msg: tr('svc.hsSoon') })}>{tr('svc.hs')}</Button>
            <button type="button" className={s.tool} aria-label={tr('svc.moreAct')} title={tr('svc.moreAct')} onClick={noWrite}><IconDots size={18} aria-hidden="true" /></button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.tabsRow}>
          <Segmented label={tr('svc.kind')} value={tab as string} onChange={(k) => setTab(k as Tab)} items={tabItems} />
        </div>
        {view === 'kanban' && ready ? (
          <div className={s.kanban}>
            {KANBAN.map((k) => {
              const col = rows.filter((r) => r.status === k);
              return (
                <section key={k} aria-label={k} className={s.col}>
                  <div className={s.colH}><span className={b.stDot} style={{ background: ST_DOT[k] }} aria-hidden="true" /><b>{k}</b><span className={s.cnt}>{col.length}</span></div>
                  {col.map((r) => (
                    <button key={r.id} type="button" className={s.kcard} onClick={() => { setView('list'); setOpen(r.id); }}>
                      <span className={s.l}><span className={s.code}>{r.code}</span><b className={s.job}>{r.job}</b></span>
                      <span className={s.step}>{r.customer} · {stepOf(r).name}</span>
                      <span className={s.sub}><span>{r.who}</span><Clock t={r} /></span>
                    </button>
                  ))}
                </section>
              );
            })}
          </div>
        ) : (
          <section className={s.pad} aria-label={tr('svc.tasks')}>
            <div className={s.filters}>
              <label className={s.search}>
                <IconSearch size={16} aria-hidden="true" />
                <input aria-label={tr('svc.search')} placeholder={tr('svc.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <div className={s.fRow}>
                <Segmented label={tr('svc.scope')} value={scope} onChange={(k) => setScope(k as Scope)} items={[{ key: 'mine', label: tr('svc.mine') }, { key: 'team', label: tr('svc.team') }]} />
                <button type="button" className={s.flt} aria-pressed={lateOnly} onClick={() => setLateOnly((v) => !v)}>{tr('svc.late')} · {lateN}</button>
                <button type="button" className={s.flt} aria-pressed={urgOnly} onClick={() => setUrgOnly((v) => !v)}>{tr('svc.urg')} · {urgN}</button>
              </div>
            </div>
            <StateView {...stateProps} state={ready && rows.length === 0 ? 'empty' : state} title={ready ? tr('svc.noMatch') : stateProps.title} icon={ready ? <IconClipboardCheck size={26} stroke={1.8} /> : stateProps.icon} desc={ready ? undefined : stateProps.desc}>
              <ul className={s.rows}>
                {rows.map((r) => (
                  <li key={r.id}>
                    <button type="button" className={s.row} onClick={() => setOpen(r.id)}>
                      <span className={s.top}>
                        <span className={s.l}>
                          <span className={s.code}>{r.code}</span>
                          <b className={s.job}>{r.job}</b>
                          {r.urgent && <span className={s.urg} title={r.urgentWhy}>{tr('svc.urg')}</span>}
                        </span>
                        <StatusChip st={r.status} />
                      </span>
                      <span className={s.step}>{r.customer} · {stepOf(r).name}</span>
                      <Bar t={r} />
                      <span className={s.sub}><span>{r.who} · {tr('svc.stepNo', { n: stepOf(r).no ?? '' })}</span><Clock t={r} /></span>
                    </button>
                  </li>
                ))}
              </ul>
            </StateView>
          </section>
        )}
      </div>
    </Page>
  );
}

/** Bảng chi tiết việc trên điện thoại: bước, điều kiện còn thiếu (bấm "Chụp" mở màn ảnh hiện trường). */
function PhoneSheet({ t, onPhoto, onNoWrite }: { t: ServiceTask; onPhoto: (slot: string) => void; onNoWrite: () => void }) {
  const tr = useT();
  const g = phoneGate(t);
  const next = t.step >= defOf(t.code).steps.length - 1 ? tr('svc.done') : defOf(t.code).steps[t.step + 1];
  return (
    <div className={s.sheet}>
      <div className={s.sTitle}><span className={s.code}>{t.code}</span><b>{defOf(t.code).name}</b><StatusChip st={t.status} /></div>
      <div className={s.step}>{t.id} · {t.job} · {t.customer}</div>
      <ol className={s.steps}>
        {stepRows(t).map((r) => (
          <li key={r.name} className={s[r.state]}><i>{r.mark}</i><span>{r.name}</span><small>{r.when}</small></li>
        ))}
      </ol>
      <div className={g.length ? s.warnTx : s.okTx}>{g.length ? tr('svc.missN', { n: g.length }) : tr('svc.ready')}</div>
      {g.map((x) => <GateRow key={x.text} g={x} onGo={() => (x.photo ? onPhoto(x.photo) : onNoWrite())} />)}
      <Button variant="primary" disabled={g.length > 0} onClick={onNoWrite}>{tr('svc.moveTo', { next: next ?? '' })}</Button>
    </div>
  );
}

function GateRow({ g, onGo }: { g: PhoneGate; onGo: () => void }) {
  return (
    <div className={s.gRow}>
      <span>{g.text}</span>
      <Button icon={g.photo ? <IconCamera size={16} aria-hidden="true" /> : undefined} onClick={onGo}>{g.go}</Button>
    </div>
  );
}

/** Ảnh hiện trường theo loại dịch vụ · chứng từ · chi phí (Mint-Service-Phone, khung 2). */
function PhotoScreen({ t, slot, onBack }: { t: ServiceTask; slot: string; onBack: () => void }) {
  const tr = useT();
  const toast = useToast();
  const [tab, setTab] = useState<'photo' | 'docs' | 'cost'>('photo');
  const photos = photoRows(t);
  const cur = photos.find((p) => p[0] === slot) ?? photos.find((p) => !p[1]);
  const miss = phoneGate(t).length;
  const d = docRows(t);
  const st = stepOf(t);
  return (
    <Page kind="table">
      <PageHeader
        title={`${t.code} · ${defOf(t.code).name}`}
        lead={<button type="button" className={s.back} aria-label={tr('svc.backTasks')} title={tr('svc.backTasks')} onClick={onBack}><IconChevronLeft size={18} aria-hidden="true" /></button>}
        sub={<span>{t.id} · {tr('svc.stepNo', { n: st.no ?? '' })} {defOf(t.code).steps[Math.min(t.step, defOf(t.code).steps.length - 1)]}</span>}
      />
      <div className={s.page}>
        <Segmented label={tr('svc.content')} value={tab} onChange={(k) => setTab(k as typeof tab)} items={[{ key: 'photo', label: tr('svc.photo') }, { key: 'docs', label: tr('svc.docs') }, { key: 'cost', label: tr('svc.cost') }]} />
        {tab === 'photo' && (
          <>
            <div className={s.slots}>
              {photos.map(([k, ok]) => <button key={k} type="button" className={s.slot} aria-pressed={k === cur?.[0]} data-ok={ok} onClick={() => toast({ msg: tr(ok ? 'svc.photoHave' : 'svc.photoNone', { k: k.toLowerCase() }) })}>{ok ? '✓' : '+'} {k}</button>)}
            </div>
            <div className={s.cam} role="img" aria-label={tr('svc.viewfinder')}>
              <div className={s.frame}><span>{tr('svc.frameIn', { k: cur ? cur[0].toLowerCase() : tr('svc.photoWord') })}</span></div>
              <span className={s.cap}>{tr('svc.camHint')}</span>
            </div>
            <div className={s.acts}>
              <Button variant="primary" icon={<IconCamera size={16} aria-hidden="true" />} onClick={() => toast({ msg: tr('svc.noWrite') })}>{tr('svc.shoot', { k: cur ? cur[0].toLowerCase() : tr('svc.photoWord') })}</Button>
              <Button icon={<IconUpload size={16} aria-hidden="true" />} onClick={() => toast({ msg: tr('svc.noWrite') })}>{tr('svc.pickFile')}</Button>
            </div>
            <div className={s.foot}><span><IconCamera size={13} aria-hidden="true" /> {photos.filter((p) => p[1]).length}/{photos.length}</span><span className={miss ? s.warnTx : s.okTx}>{miss ? tr('svc.missNl', { n: miss }) : tr('svc.okl')}</span></div>
          </>
        )}
        {tab === 'docs' && (
          <ul className={s.docs}>
            <li className={s.docsH}>{tr('svc.needDocs')} <b>{d.done}</b></li>
            {d.rows.map(([k, v]) => <li key={k}><span>{k}</span><span className={v === 'còn thiếu' ? s.warnTx : ''}>{v}</span></li>)}
          </ul>
        )}
        {tab === 'cost' && <div className={s.note}>{tr('svc.moneyNote')}</div>}
      </div>
    </Page>
  );
}

/** Trang chi tiết iPad 2:1 (Mint-Service-iPad-Detail): trái = Lô hàng · Điều kiện · Quy trình · Hồ sơ; phải = Việc cần làm · Thông tin chính. */
function PadDetail({ t, onBack }: { t: ServiceTask; onBack: () => void }) {
  const tr = useT();
  const toast = useToast();
  const [dtab, setDtab] = useState<'steps' | 'ai' | 'files' | 'mail'>('steps');
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const def = defOf(t.code);
  const kpi = kpiOf(t);
  const steps = stepRows(t);
  const docs = docRows(t);
  const photos = photoRows(t);
  const gate = phoneGate(t);
  const fin = t.status === 'Finish';
  const cur = def.steps[Math.min(t.step, def.steps.length - 1)];
  const next = t.step >= def.steps.length - 1 ? tr('svc.done') : def.steps[t.step + 1];
  const miss = docs.rows.filter((r) => r[1] === 'còn thiếu').length;
  const phMiss = photos.filter((p) => !p[1]).length;
  const todo: { k: string; sub: string; done?: boolean; act?: string }[] = [
    ...gate.map((x) => ({ k: x.text, sub: tr('svc.before', { next: next ?? '' }), act: x.go })),
    fin ? { k: tr('svc.svcDone'), sub: '', done: true } : { k: tr('svc.curStep', { cur: cur ?? '' }), sub: tr('svc.dueS', { d: t.due.slice(0, 6) }), act: 'Chuyển bước' },
    ...(miss ? [{ k: tr('svc.addDocs', { n: miss }), sub: `${tr('svc.needDocs')} ${docs.done}`, act: 'Xem' }] : []),
    ...(phMiss ? [{ k: tr('svc.shootN', { n: phMiss }), sub: `${photos.length - phMiss}/${photos.length}`, act: 'Chụp' }] : []),
  ];
  const info: [string, string][] = [['Job', t.job], [tr('svc.i.id'), t.id], ['Booking', t.booking], [tr('svc.i.cust'), t.customer], [tr('svc.i.vessel'), `${t.carrier} · ${t.vessel}`], [tr('svc.i.route'), t.route]];
  const head = (k: string, title: string, icon: React.ReactNode, badge?: string) => (
    <div className={s.sh}>
      <span className={s.ico}>{icon}</span>
      <h2>{title}</h2>
      <span className={s.ico} role="img" aria-label={tr('svc.explain', { title })} title={title}><IconInfoCircle size={15} aria-hidden="true" /></span>
      {badge && <span className={s.badge}>{badge}</span>}
      <span className={s.grow} />
      <button type="button" className={s.tog} aria-expanded={!shut[k]} aria-label={tr('svc.toggle', { title })} title={tr('svc.toggleT')} onClick={() => setShut((o) => ({ ...o, [k]: !o[k] }))}>
        <IconChevronDown size={18} aria-hidden="true" />
      </button>
    </div>
  );
  return (
    <Page kind="table">
      <PageHeader
        title={def.name}
        lead={<button type="button" className={s.back} aria-label={tr('svc.backSvc')} title={tr('svc.backSvc')} onClick={onBack}><IconChevronLeft size={18} aria-hidden="true" /></button>}
        sub={<span>{[t.id, t.job, t.customer, t.ref, t.who, t.auto ? tr('svc.auto') : ''].filter(Boolean).join(' · ')}</span>}
        meta={
          <>
            <span className={s.code}>{t.code}</span>
            <StatusChip st={t.status} />
            <span className={`${s.hdrChip} ${leftTone(t)}`} title={tr('svc.dueHint', { kpi })}>{tr('svc.due')} {t.due.replace('/2026', '')} · {leftLabel(t)}</span>
            {t.urgent && <span className={s.urg} title={t.urgentWhy}>{tr('svc.urg')}</span>}
          </>
        }
        actions={
          <>
            <button type="button" className={s.tool} aria-label={tr('svc.otherOps')} title={tr('svc.otherOps')} onClick={() => toast({ msg: tr('svc.noWrite') })}><IconDots size={18} aria-hidden="true" /></button>
            <Button variant="primary" disabled={fin} onClick={() => toast({ msg: tr('svc.noWrite') })}>{fin ? tr('svc.finished') : t.step >= def.steps.length - 1 ? tr('svc.complete') : tr('svc.advance')}</Button>
          </>
        }
      />
      <div className={s.dpage}>
        <Segmented label={tr('svc.svcContent')} value={dtab} onChange={(k) => setDtab(k as typeof dtab)} items={[{ key: 'steps', label: tr('svc.process') }, { key: 'ai', label: tr('svc.docsAi') }, { key: 'files', label: tr('svc.files') }, { key: 'mail', label: tr('svc.mail') }]} />
        <div className={s.detail}>
          <div className={s.col2}>
            {dtab === 'steps' && (
              <>
                <section className={s.sec} aria-label={tr('svc.shipment')}>
                  {head('ship', tr('svc.shipment'), <IconPackage size={18} aria-hidden="true" />)}
                  {!shut.ship && <dl className={s.ship}>{shipRows(t).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>}
                </section>
                {gate.length > 0 && (
                  <section className={s.sec} aria-label={tr('svc.gate')}>
                    {head('gate', tr('svc.gate'), <IconClipboardCheck size={18} aria-hidden="true" />, String(gate.length))}
                    {!shut.gate && <div className={s.body}><div className={s.warnTx}>{tr('svc.cannot', { next: next ?? '' })}</div>{gate.map((x) => <GateRow key={x.text} g={x} onGo={() => toast({ msg: tr('svc.noWrite') })} />)}</div>}
                  </section>
                )}
                <section className={s.sec} aria-label={tr('svc.process')}>
                  {head('steps', tr('svc.process'), <IconListDetails size={18} aria-hidden="true" />, `${Math.min(t.step + 1, def.steps.length)}/${def.steps.length}`)}
                  {!shut.steps && (
                    <ol className={s.tl}>
                      {steps.map((r) => (
                        <li key={r.name} className={s[r.state]}>
                          <i>{r.mark}</i>
                          <div><div className={s.tlTop}><b>{r.name}</b><span>{r.when}</span></div><div className={s.tlSub}>{r.who} · {tr('svc.sopStep')}</div></div>
                        </li>
                      ))}
                    </ol>
                  )}
                </section>
                <section className={s.sec} aria-label={tr('svc.needDocs')}>
                  {head('docs', tr('svc.needDocs'), <IconFileText size={18} aria-hidden="true" />, docs.done)}
                  {!shut.docs && <ul className={s.docs}>{docs.rows.map(([k, v]) => <li key={k}><span>{k}</span><span className={v === 'còn thiếu' ? s.warnTx : ''}>{v}</span></li>)}</ul>}
                </section>
              </>
            )}
            {dtab === 'ai' && (
              <section className={s.sec} aria-label={tr('svc.profile')}>
                {head('prof', tr('svc.profileOf', { name: def.name }), <IconFileText size={18} aria-hidden="true" />)}
                <div className={s.note}>{tr('svc.profNote')}</div>
              </section>
            )}
            {dtab === 'files' && (
              <section className={s.sec} aria-label={tr('svc.sitePhotos')}>
                {head('photos', tr('svc.sitePhotos'), <IconCamera size={18} aria-hidden="true" />, `${photos.length - phMiss}/${photos.length}`)}
                <div className={s.photos}>{photos.map(([k, ok]) => <button key={k} type="button" className={s.pbtn} data-ok={ok} onClick={() => toast({ msg: tr('svc.noWrite') })}><span>{ok ? '✓' : '+'}</span>{k}</button>)}</div>
                {photos.length === 0 && <div className={s.note}>{tr('svc.noPhotos')}</div>}
              </section>
            )}
            {dtab === 'mail' && <section className={s.sec} aria-label={tr('svc.svcMail')}>{head('mail', tr('svc.mail'), <IconFileText size={18} aria-hidden="true" />)}<div className={s.note}>{tr('svc.noSend')}</div></section>}
          </div>
          <div className={s.col2}>
            <section className={s.sec} aria-label={tr('svc.todo')}>
              {head('todo', tr('svc.todo'), <IconClipboardCheck size={18} aria-hidden="true" />, String(todo.filter((x) => !x.done).length))}
              {!shut.todo && (
                <ul className={s.todo}>
                  {todo.map((x) => (
                    <li key={x.k} className={x.done ? s.tdone : ''}>
                      <i aria-hidden="true">{x.done ? '✓' : ''}</i>
                      <div><div className={s.tk}>{x.k}</div><div className={s.tlSub}>{x.sub ? `${t.who} · ${x.sub}` : t.who}</div></div>
                      {x.act && !x.done && <Button icon={x.act === 'Xem' ? <IconEye size={16} aria-hidden="true" /> : undefined} onClick={() => toast({ msg: tr('svc.noWrite') })}>{x.act && ACT[x.act] ? tr(ACT[x.act]!) : x.act}</Button>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className={s.sec} aria-label={tr('svc.info')}>
              {head('info', tr('svc.info'), <IconListDetails size={18} aria-hidden="true" />)}
              {!shut.info && <dl className={s.dl}>{info.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>}
            </section>
          </div>
        </div>
      </div>
    </Page>
  );
}

